export interface LuxorMedResponse {
  responseType: 'luxor-medical-report' | 'luxor-patient-case-analysis' | 'luxor-patient-guidance';
  responseContent: string;
  promptContent: string;
  timestamp: string;
  appointmentId?: string;
  promptVersion?: string;
  agentProvider?: string;
}

export interface LuxorMedHistory {
  responses: LuxorMedResponse[];
  sessionId?: string;
  patientId?: string;
  practitionerId?: string;
}

export const saveLuxorMedResponse = (response: LuxorMedResponse) => {
  try {
    const existingHistory = getLuxorMedHistory();

    const responseWithDefaults: LuxorMedResponse = {
      ...response,
      promptVersion: response.promptVersion || "1.0.0",
      agentProvider: response.agentProvider || "OpenAI"
    };

    existingHistory.responses.push(responseWithDefaults);

    saveHistoryLuxorMed(existingHistory);

    return true;
  } catch (error) {
    console.error('Erro ao salvar resposta do LuxorMed:', error);
    return false;
  }
};

export const getLuxorMedHistory = (): LuxorMedHistory => {
  try {
    const stored = localStorage.getItem("historyLuxorMed");
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.history || { responses: [] };
    }
    return { responses: [] };
  } catch (error) {
    console.error('Erro ao obter histórico do LuxorMed:', error);
    return { responses: [] };
  }
};

export const saveHistoryLuxorMed = (history: LuxorMedHistory) => {
  const historyData = {
    history,
  };
  localStorage.setItem("historyLuxorMed", JSON.stringify(historyData));
};

export const clearLuxorMedHistory = () => {
  localStorage.removeItem("historyLuxorMed");
};

export const getLuxorMedSessionResponses = (sessionId: string): LuxorMedResponse[] => {
  const history = getLuxorMedHistory();
  if (history.sessionId === sessionId) {
    return history.responses;
  }
  return [];
};

export const generateQuestionnaireResponse = (sessionId?: string): any => {
  const history = getLuxorMedHistory();

  const questionnaireResponse: any = {
    resourceType: "QuestionnaireResponse",
    questionnaire: "Questionnaire/luxor-med-history",
    status: "completed",
    authored: new Date().toISOString(),
    subject: {
      reference: history.patientId ? `Patient/${history.patientId}` : undefined
    },
    author: {
      reference: history.practitionerId ? `Practitioner/${history.practitionerId}` : undefined
    },
    item: []
  };

  history.responses.forEach((response, index) => {
    const responseItem = {
      linkId: "luxor-response-group",
      text: "Respostas do LuxorMed",
      item: [
        {
          linkId: "agent-type",
          text: "Tipo de Agente",
          answer: [
            {
              valueString: getResponseTypeDisplay(response.responseType)
            }
          ]
        },
        {
          linkId: "response-content",
          text: "Conteúdo da Resposta",
          answer: [
            {
              valueString: response.responseContent
            }
          ]
        },
        {
          linkId: "question-content",
          text: "Conteúdo da Pergunta",
          answer: [
            {
              valueString: response.promptContent
            }
          ]
        },
        {
          linkId: "prompt-version",
          text: "Versão do Prompt",
          answer: [
            {
              valueString: response.promptVersion || "1.0.0"
            }
          ]
        },
        {
          linkId: "response-timestamp",
          text: "Data e Hora",
          answer: [
            {
              valueDateTime: response.timestamp
            }
          ]
        },
        {
          linkId: "agent-provider",
          text: "Fornecedor",
          answer: [
            {
              valueString: response.agentProvider || "OpenAI"
            }
          ]
        },
        ...(response.appointmentId ? [{
          linkId: "appointment-id",
          text: "ID do Atendimento",
          answer: [
            {
              valueString: response.appointmentId
            }
          ]
        }] : [])
      ]
    };

    questionnaireResponse.item.push(responseItem);
  });

  return questionnaireResponse;
};

const getResponseTypeDisplay = (responseType: string): string => {
  const displays = {
    'luxor-medical-report': 'Relatório Médico',
    'luxor-patient-case-analysis': 'Discussão de Casos',
    'luxor-patient-guidance': 'Orientações ao Paciente'
  };
  return displays[responseType as keyof typeof displays] || responseType;
};

export const initializeLuxorMedSession = (patientId?: string, practitionerId?: string) => {
  const sessionId = `luxor-session-${Date.now()}`;
  const newHistory: LuxorMedHistory = {
    responses: [],
    sessionId,
    patientId,
    practitionerId
  };

  saveHistoryLuxorMed(newHistory);
  return sessionId;
};